import React, { useEffect, useState } from 'react';

import {
  ListCard,
  ListType,
} from '../../../helpers/list-page/interfaces/list-card.interface';
import {
  cardIsEditorial,
  cardIsEvent,
  cardIsLeisurePackageOffer,
  cardIsNewsItem,
  cardIsOffer,
  cardIsService,
  cardIsServiceSubCategory,
  cardIsTenant,
} from '../../../helpers/list-page/type-guards';
import IkeaProductCardInHomePage from '../home-page/ikea-product-card';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';
import { ILibraryTenantAttributeFilters } from '@/lib/fragments/libraryTenantAttributeFilters';
import { PageContext } from '@/templates/page-context.interface';
import { CardOffer } from '@/components/cards/card-offer';
import { CardEvent } from '@/components/cards/card-event';
import { CardEditorialItem } from '@/components/cards/card-editorial-item';
import { CardTenant } from '@/components/cards/card-tenant';
import { CardService } from '@/components/cards/card-service';
import { CardLeisurePackageOffer } from '@/components/cards/card-leisure-package-offer';
import { CardServiceSubCategory } from '@/components/cards/card-service-sub-category';
import { CardNewsItem } from '@/components/cards/card-news-item';
import { CardIkeaFamilyBenefit } from '@/components/cards/card-ikea-family-benefit';
import type { IkeaFamilyBenefitItem } from '@/lib/getIkeaFamilyBenefits';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { WithCountdown } from '@/components/cards/wrapper/WithCountdown';

interface TileViewProps {
  listArray: ListCard[];
  dictionary?: ILibraryMeetingPlaceDictionary;
  tileColClass: string;
  attributeFilters?: ILibraryTenantAttributeFilters;
  listType?: ListType;
  node_locale: string;
  pageContext?: PageContext;
  businessUnitCode?: string;
  pageName: string;
  config: any;
  ikeaFamilyBenefit?: IkeaFamilyBenefitItem | null;
}

export const TileView = ({
  listArray,
  dictionary,
  tileColClass,
  attributeFilters,
  listType,
  node_locale,
  pageContext,
  businessUnitCode,
  pageName,
  config,
  ikeaFamilyBenefit,
}: TileViewProps) => {
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY == 'true';
  const [columns, setColumns] = useState(3);
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width >= 1440) setColumns(4);
      else if (width >= 1024) setColumns(3);
      else if (width >= 768) setColumns(2);
      else setColumns(1);
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);
  const renderCard = (card: ListCard, cardIndex: number) => {
    if (cardIsTenant(card)) {
      return (
        <CardTenant
          card={card}
          cardIndex={cardIndex}
          key={card?.id}
          attributeFilters={attributeFilters}
        />
      );
    }
    if (cardIsOffer(card)) {
      return (
        <WithCountdown key={card?.id} card={card}>
          {({ countdownState }) => (
            <CardOffer
              card={card}
              cardIndex={cardIndex}
              dictionary={dictionary}
              countdownState={countdownState}
            />
          )}
        </WithCountdown>
      );
    }
    if (cardIsEvent(card)) {
      return (
        <CardEvent
          card={card}
          cardIndex={cardIndex}
          key={card?.id}
          dictionary={dictionary}
          isLivliFamily={isLivliFamily}
        />
      );
    }
    if (cardIsNewsItem(card)) {
      return <CardNewsItem card={card} cardIndex={cardIndex} key={card?.id} />;
    }
    if (cardIsService(card)) {
      return <CardService card={card} cardIndex={cardIndex} key={card?.id} />;
    }
    if (cardIsEditorial(card)) {
      return (
        <CardEditorialItem card={card} cardIndex={cardIndex} key={card?.id} />
      );
    }
    if (cardIsLeisurePackageOffer(card)) {
      return (
        <CardLeisurePackageOffer
          card={card}
          cardIndex={cardIndex}
          key={card?.id}
          dictionary={dictionary}
        />
      );
    }
    if (cardIsServiceSubCategory(card)) {
      return (
        <CardServiceSubCategory
          card={card}
          cardIndex={cardIndex}
          key={card.id}
        />
      );
    }
  };
  const firstRow = listArray.slice(0, columns);
  const rest = listArray.slice(columns);
  const showIkeaProductCard =
    config.NEXT_PUBLIC_IKEA_PRODUCT__IS_SHOP_LISTING_PAGE &&
    config.NEXT_PUBLIC_IKEA_PRODUCT__IS_SHOP_LISTING_PAGE === 'true' &&
    listType == 'shop'
      ? true
      : false;

  const updatedPageName = pageName + (pageContext?.htmlTitleSuffix ?? '');

  type LayoutMode = 'ikea' | 'masonry-mobile' | 'grid';

  const getLayoutMode = (
    listType?: ListType,
    showIkeaProductCard?: boolean,
  ): LayoutMode => {
    if (showIkeaProductCard) return 'ikea';
    if (listType === 'offer') return 'masonry-mobile';
    return 'grid';
  };

  const layoutMode = getLayoutMode(listType, showIkeaProductCard);

  const baseCards = listArray?.map((card, index) => (
    <React.Fragment key={`tile-${card?.id || index}`}>
      {renderCard(card, index)}
    </React.Fragment>
  ));

  // Inject the IKEA Family benefit card at the 3rd grid position (index 2)
  // across all breakpoints; if there are fewer than 3 cards, append it.
  const allCards =
    ikeaFamilyBenefit && baseCards?.length
      ? (() => {
          const insertAt = Math.min(2, baseCards.length);
          const familyCardNode = (
            <React.Fragment key="ikea-family-benefit">
              <CardIkeaFamilyBenefit
                card={ikeaFamilyBenefit}
                listType={listType}
                pageName={updatedPageName}
              />
            </React.Fragment>
          );
          return [
            ...baseCards.slice(0, insertAt),
            familyCardNode,
            ...baseCards.slice(insertAt),
          ];
        })()
      : baseCards;

  if (layoutMode === 'ikea') {
    return (
      <div className={`mt-5 grid gap-5 ${tileColClass}`}>
        {firstRow.map((card, index) => (
          <React.Fragment key={`first-${card?.id || index}`}>
            {renderCard(card, index)}
          </React.Fragment>
        ))}
        <div
          key="product-card"
          className={`${columns !== 1 ? 'col-span-2' : 'col-span-1'} min-h-[140px] rounded-md`}
        >
          <IkeaProductCardInHomePage
            node_locale={node_locale || ''}
            primaryLanguage={pageContext?.languages?.[0]}
            businessUnitCode={businessUnitCode}
            buyNowText={dictionary?.buyAtIkea || ''}
            variant={columns != 1 ? 'listing' : 'homepage'}
            cardHeader={dictionary?.featuredIkeaProduct}
            pageName={updatedPageName}
          />
        </div>
        {rest.map((card, index) => (
          <React.Fragment key={`rest-${card?.id || index}`}>
            {renderCard(card, index + columns + (columns - 1))}
          </React.Fragment>
        ))}
      </div>
    );
  }

  if (layoutMode === 'masonry-mobile') {
    return (
      <div className="mt-5">
        {/* Mobile: Masonry */}
        <div className="block sm:hidden">
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 0: 2 }}
            gutterBreakPoints={{ 350: 8 }}
          >
            <Masonry itemStyle={{ gap: '16px' }}>{allCards}</Masonry>
          </ResponsiveMasonry>
        </div>
        {/* Desktop: Grid */}
        <div className={`hidden sm:grid gap-5 ${tileColClass}`}>{allCards}</div>
      </div>
    );
  }

  // layoutMode === 'grid' (default)
  return <div className={`mt-5 grid gap-5 ${tileColClass}`}>{allCards}</div>;
};

TileView.displayName = 'TileView';

